import { BarSegment, Chart, useChart } from '@chakra-ui/charts';
import { AbsoluteCenter, Box, Grid, GridItem, HStack, Heading, SimpleGrid, Skeleton, Stat } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { Bar, BarChart, CartesianGrid, Cell, Legend, Tooltip, XAxis, YAxis } from 'recharts';

import { type ActionsStat, StatsService } from '@/client';

const hashTextToNumber = (text: string) => {
  const hash = text.split('').reduce((acc, char) => {
    return (acc << 5) - acc + char.charCodeAt(0); // Simple hash function
  }, 0);
  return Math.abs(hash);
};

const getColorForText = (text: string, shade = '500') => {
  const colors = ['teal', 'purple', 'blue', 'green', 'red', 'yellow', 'orange', 'pink'];
  const hashValue = hashTextToNumber(text);
  const colorIndex = hashValue % colors.length;
  return `${colors[colorIndex]}.${shade}`;
};

const StatsCards = ({ statsData, isLoading }: { statsData: ActionsStat | undefined; isLoading: boolean }) => {
  if (!statsData) {
    return null;
  }

  return (
    <>
      <SimpleGrid columns={{ base: 1, md: 3 }} gap={4} px={4}>
        <Stat.Root borderWidth="1px" p="4" rounded="md" bg="primary.500">
          <Stat.Label fontSize="lg" color={'white'}>
            Nombre d'actions proritaires
          </Stat.Label>
          <Skeleton width="full" loading={isLoading}>
            <HStack>
              <Stat.ValueText textWrap={'nowrap'} fontSize="3xl" color={'white'}>
                {statsData.priority_count} / {statsData.actions_count}
              </Stat.ValueText>
              <Stat.HelpText color={'white'}>
                {Math.floor(((statsData.priority_count as number) / (statsData.actions_count as number)) * 100)} % des
                actions sont prioritaires
              </Stat.HelpText>
            </HStack>
          </Skeleton>
        </Stat.Root>

        <Stat.Root borderWidth="1px" p="4" rounded="md" bg="primary.500">
          <Stat.Label fontSize="lg" color={'white'}>
            Nombre d'actions archivées
          </Stat.Label>
          <Skeleton width="full" loading={isLoading}>
            <HStack>
              <Stat.ValueText textWrap={'nowrap'} fontSize="3xl" color={'white'}>
                {statsData.archived_count} / {statsData.actions_count}
              </Stat.ValueText>
              <Stat.HelpText color={'white'}>
                {Math.floor(((statsData.archived_count as number) / (statsData.actions_count as number)) * 100)} % des
                actions sont archivées
              </Stat.HelpText>
            </HStack>
          </Skeleton>
        </Stat.Root>

        <Stat.Root borderWidth="1px" p="4" rounded="md" bg="primary.500">
          <Stat.Label fontSize="lg" color={'white'}>
            Nombre d'actions verouillées
          </Stat.Label>
          <Skeleton width="full" loading={isLoading}>
            <HStack>
              <Stat.ValueText textWrap={'nowrap'} fontSize="3xl" color={'white'}>
                {statsData.locked_count} / {statsData.actions_count}
              </Stat.ValueText>
              <Stat.HelpText color={'white'}>
                {Math.floor(((statsData.locked_count as number) / (statsData.actions_count as number)) * 100)} % des
                actions sont archivées
              </Stat.HelpText>
            </HStack>
          </Skeleton>
        </Stat.Root>
      </SimpleGrid>
      <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} px={4} mt={4}>
        <Stat.Root borderWidth="1px" p="4" rounded="md" bg="primary.500">
          <Stat.Label fontSize="lg" color={'white'}>
            Investissement financier
          </Stat.Label>
          <Skeleton width="full" loading={isLoading}>
            <HStack>
              <Stat.ValueText textWrap={'nowrap'} fontSize="3xl" color={'white'}>
                {statsData.total_monetary_cost} €
              </Stat.ValueText>
              <Stat.HelpText color={'white'}>
                {Math.floor(
                  ((statsData.with_monetary_cost_count as number) / (statsData.actions_count as number)) * 100
                )}{' '}
                % des actions renseignées
              </Stat.HelpText>
            </HStack>
          </Skeleton>
        </Stat.Root>

        <Stat.Root borderWidth="1px" p="4" rounded="md" bg="primary.500">
          <Stat.Label fontSize="lg" color={'white'}>
            Investissement humain
          </Stat.Label>
          <Skeleton width="full" loading={isLoading}>
            <HStack>
              <Stat.ValueText textWrap={'nowrap'} fontSize="3xl" color={'white'}>
                {statsData.total_human_cost} jours
              </Stat.ValueText>
              <Stat.HelpText color={'white'}>
                {Math.floor(((statsData.with_human_cost_count as number) / (statsData.actions_count as number)) * 100)}{' '}
                % des actions renseignées
              </Stat.HelpText>
            </HStack>
          </Skeleton>
        </Stat.Root>
      </SimpleGrid>
    </>
  );
};

const BarSegmentsDistribution = ({ segments, title }: { segments: [string, number][]; title: string }) => {
  const chart = useChart({
    sort: { by: 'value', direction: 'desc' },
    data: segments.map(([name, value]) => ({
      name,
      value,
      color: getColorForText(name),
    })),
  });

  return (
    <Box>
      <Heading size={'lg'} mb={4}>
        {title}
      </Heading>
      <BarSegment.Root chart={chart} barSize="6">
        <BarSegment.Content>
          <BarSegment.Bar gap="0.5" tooltip />
        </BarSegment.Content>
        <BarSegment.Legend showPercent />
      </BarSegment.Root>
    </Box>
  );
};

function getActionsStatsQueryOptions({ slug }: { slug: string }) {
  return {
    queryKey: ['stats', { slug }],
    queryFn: async () => {
      return await StatsService.readActionsStats({
        companySlug: slug,
      });
    },
  };
}

const BarChartProgressionPerImpact = ({ data, title }: { data: [string, number][]; title: string }) => {
  const chart = useChart({
    sort: { by: 'value', direction: 'desc' },
    data: data
      .sort((a, b) => (a[1] ?? 0) - (b[1] ?? 0))
      .map((item) => ({
        name: item[0],
        value: (item[1] ?? 0) / 100,
        color: getColorForText(item[0]),
      })),
  });

  return (
    <>
      <Heading size={'lg'} mb={4}>
        {title}
      </Heading>
      <Chart.Root maxH="sm" chart={chart}>
        <BarChart stackOffset="expand" layout="vertical" data={chart.data}>
          <CartesianGrid stroke={chart.color('border.muted')} vertical={false} />
          <XAxis
            type="number"
            axisLine={false}
            tickLine={false}
            tickFormatter={chart.formatNumber({ style: 'percent' })}
          />
          <YAxis type="category" dataKey={chart.key('name')} orientation="left" stroke={chart.color('border')} />
          <Tooltip cursor={{ fill: chart.color('bg.muted') }} animationDuration={100} content={<Chart.Tooltip />} />
          <Legend content={<Chart.Legend />} />
          <Bar barSize={30} isAnimationActive={false} dataKey={chart.key('value')}>
            {chart.data.map((item) => (
              <Cell key={chart.key('name')} fill={chart.color(item.color)} />
            ))}
          </Bar>
        </BarChart>
      </Chart.Root>
    </>
  );
};

export const DashboardActionsView = () => {
  const { slug } = useParams({ strict: false });

  const { data: statsData, isLoading } = useQuery({
    ...getActionsStatsQueryOptions({
      slug,
    }),
  });

  return (
    <>
      <StatsCards statsData={statsData} isLoading={isLoading} />
      <SimpleGrid columns={{ base: 1, lg: 2 }} gap="4" p="4">
        <BarSegmentsDistribution
          segments={statsData?.departments || []}
          title="Distributions des actions par departements"
        />
        <BarSegmentsDistribution segments={statsData?.impacts || []} title="Distributions des actions par impacts" />
      </SimpleGrid>
      <Grid templateColumns="repeat(4, 1fr)" gap="4" p="4">
        <GridItem colSpan={{ base: 4, md: 1 }}>
          <Stat.Root borderWidth="1px" p="4" rounded="md" bg="primary.500" height={'full'}>
            <Stat.Label fontSize="lg" color={'white'}>
              Progression Global
            </Stat.Label>
            <AbsoluteCenter axis={{ base: 'horizontal', md: 'both' }} pos={{ base: 'relative', md: 'absolute' }}>
              <Skeleton width="full" loading={isLoading}>
                <HStack>
                  <Stat.ValueText textWrap={'nowrap'} fontSize={{ base: '3xl', md: '4xl', lg: '6xl' }} color={'white'}>
                    {statsData?.global_progression} %
                  </Stat.ValueText>
                </HStack>
              </Skeleton>
            </AbsoluteCenter>
          </Stat.Root>
        </GridItem>
        <GridItem colSpan={{ base: 4, md: 3 }}>
          <BarChartProgressionPerImpact data={statsData?.progression_per_impact || []} title="Progression par impact" />
        </GridItem>
      </Grid>
    </>
  );
};

export default DashboardActionsView;
